import { Loader2, Mic, SendHorizontal } from 'lucide-react';
import { useCallback, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import { Textarea } from '@apps/web/components/ui/textarea';
import { useVoiceInput } from '@apps/web/hooks/use_voice_input';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';

/**
 * Free-text availability entry: the instruction is parsed by an LLM into
 * one-time and weekly availability records. Owns the text, parse, and
 * voice-input state so the profile only reacts to the resulting refetch.
 */
export function AvailabilityInstructionComposer({
    organizationId,
    caregiverId,
    onParsed,
}: {
    readonly organizationId: string | null;
    readonly caregiverId: string | null;
    readonly onParsed: () => void;
}) {
    const [instruction, setInstruction] = useState('');
    const [isParsing, setIsParsing] = useState(false);

    const handleParse = useCallback(async () => {
        if (!organizationId || !caregiverId || isParsing) {
            return;
        }
        const trimmed = instruction.trim();
        if (!trimmed) {
            return;
        }
        setIsParsing(true);
        try {
            const response = await getApiClient().post({
                url: '/organizations/{organization_id}/caregivers/{caregiver_id}/availability/parse',
                pathParams: {
                    organization_id: organizationId,
                    caregiver_id: caregiverId,
                },
                body: { instruction: trimmed },
            });
            setInstruction('');
            toast.success(response.summary, { persistNotification: false });
            onParsed();
        } catch (error) {
            toast.error(
                error instanceof Error && error.message
                    ? error.message
                    : 'Failed to parse availability instruction',
                { persistNotification: false },
            );
        } finally {
            setIsParsing(false);
        }
    }, [organizationId, caregiverId, isParsing, instruction, onParsed]);

    const {
        canRecordAudio,
        isRecording,
        isTranscribing,
        voiceInputError,
        startRecording,
        stopRecording,
    } = useVoiceInput({
        organizationId: organizationId ?? '',
        currentText: instruction,
        onTranscribed: setInstruction,
        composer: 'caregiver_availability',
        voiceInputFlow: 'caregiver_availability_instruction',
        disabled: !organizationId || !caregiverId || isParsing,
    });

    return (
        <div className="min-w-0 flex-1 space-y-2">
            <div className="flex items-center gap-2">
                <Textarea
                    value={instruction}
                    onChange={(e) => setInstruction(e.target.value)}
                    placeholder={
                        isRecording
                            ? 'Listening...'
                            : 'Describe availability changes...'
                    }
                    maxLength={500}
                    disabled={isParsing || isRecording || isTranscribing}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            void handleParse();
                        }
                    }}
                    className="min-h-9 min-w-[12rem] flex-1 resize-none"
                />
                {canRecordAudio ? (
                    <Button
                        type="button"
                        variant="outline"
                        size="icon"
                        className="shrink-0"
                        aria-label={
                            isTranscribing
                                ? 'Transcribing voice input'
                                : isRecording
                                  ? 'Stop recording'
                                  : 'Record voice input'
                        }
                        disabled={isTranscribing || (!isRecording && isParsing)}
                        onClick={() => {
                            if (isRecording) {
                                stopRecording();
                                return;
                            }
                            void startRecording();
                        }}
                    >
                        {isTranscribing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <span className="relative flex items-center justify-center">
                                <Mic className="h-4 w-4" />
                                {isRecording ? (
                                    <span className="absolute -top-0.5 -right-0.5 h-1.5 w-1.5 animate-pulse rounded-full bg-red-500" />
                                ) : null}
                            </span>
                        )}
                    </Button>
                ) : null}
                <Button
                    type="button"
                    size="icon"
                    className="shrink-0"
                    disabled={
                        isParsing ||
                        isRecording ||
                        isTranscribing ||
                        instruction.trim().length === 0
                    }
                    onClick={() => void handleParse()}
                    aria-label="Parse availability instruction"
                >
                    {isParsing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <SendHorizontal className="h-4 w-4" />
                    )}
                </Button>
            </div>
            {voiceInputError ? (
                <p className="text-destructive text-xs">{voiceInputError}</p>
            ) : null}
        </div>
    );
}
